import { useEffect, useState } from "react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";
import ImagePicker from "../components/ImagePicker";
import ListEditor from "../components/ListEditor";

const CATEGORIES = ["Meghalaya", "Assam", "Arunachal Pradesh", "Travel Tips", "Adventure", "Food & Culture", "Road Trips"];

const EMPTY = { title: "", category: "Meghalaya", excerpt: "", cover: "", content: "", tags: [], seoTitle: "", metaDescription: "", readTime: 5, published: false, featured: false, author: "RandZ Tours & Travels" };

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/articles").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const openEdit = async (slug) => {
    const { data } = await adminApi.get(`/admin/articles/${slug}`);
    setEditing({ ...EMPTY, ...data });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing.title) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...editing, readTime: Number(editing.readTime) || 5 };
    try {
      if (isNew) await adminApi.post("/admin/articles", payload);
      else await adminApi.put(`/admin/articles/${editing.slug}`, payload);
      toast.success(payload.published ? "Saved & published" : "Saved as draft");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await adminApi.delete(`/admin/articles/${slug}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const togglePublish = async (a) => {
    const { data } = await adminApi.get(`/admin/articles/${a.slug}`);
    await adminApi.put(`/admin/articles/${a.slug}`, { ...data, published: !a.published });
    load();
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Blog Manager</h1>
          <p className="text-sm text-muted-foreground">{items.length} posts · published posts appear in the Travel Journal</p>
        </div>
        <Button onClick={() => { setEditing({ ...EMPTY }); setIsNew(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-article-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New post
        </Button>
      </div>

      <div className="rounded-xl border bg-white dark:bg-slate-900 divide-y">
        {items.map((a) => (
          <div key={a.slug} className="flex items-center gap-4 p-4" data-testid={`article-row-${a.slug}`}>
            <div className="h-14 w-20 rounded-lg overflow-hidden bg-muted shrink-0">
              {a.cover && <img src={resolveImg(a.cover)} alt="" className="h-full w-full object-cover" loading="lazy" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.title}</div>
              <div className="text-xs text-muted-foreground">{a.category} · {a.date} · {a.readTime} min</div>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={!!a.published} onCheckedChange={() => togglePublish(a)} data-testid={`publish-toggle-${a.slug}`} />
              {a.published ? "Published" : "Draft"}
            </label>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(a.slug)} data-testid={`edit-article-${a.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(a.slug, a.title)} data-testid={`delete-article-${a.slug}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="article-editor">
          <DialogHeader><DialogTitle>{isNew ? "New blog post" : `Edit — ${editing?.title}`}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-5">
              <div className="grid gap-2"><Label>Title</Label><Input value={editing.title} onChange={set("title")} data-testid="article-field-title" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="grid gap-2"><Label>Category</Label>
                  <Select value={editing.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="article-field-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2"><Label>Read time (min)</Label><Input type="number" value={editing.readTime} onChange={set("readTime")} /></div>
                <label className="flex items-center gap-3 text-sm pt-6">
                  <Switch checked={!!editing.published} onCheckedChange={set("published")} data-testid="article-field-published" /> Published
                </label>
              </div>
              <div className="grid gap-2"><Label>Excerpt</Label><Textarea rows={2} value={editing.excerpt} onChange={set("excerpt")} data-testid="article-field-excerpt" /></div>
              <ImagePicker label="Cover image" value={editing.cover} onChange={set("cover")} folder="blog" />
              <div className="grid gap-2">
                <Label>Content</Label>
                <div className="bg-white text-slate-900 rounded-lg [&_.ql-container]:min-h-[220px]" data-testid="article-field-content">
                  <ReactQuill theme="snow" value={editing.content || ""} onChange={set("content")} modules={QUILL_MODULES} />
                </div>
              </div>
              <ListEditor label="Tags" items={editing.tags || []} onChange={set("tags")} placeholder="e.g. waterfalls" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2"><Label>SEO title</Label><Input value={editing.seoTitle || ""} onChange={set("seoTitle")} data-testid="article-field-seo" /></div>
                <div className="grid gap-2"><Label>Meta description</Label><Input value={editing.metaDescription || ""} onChange={set("metaDescription")} data-testid="article-field-meta" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-article-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
